import { useEffect, useRef } from 'react';
import type { User } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import { getStoredRef } from '@/lib/affiliateRef';

/**
 * Ordnet den eingeloggten User einem Affiliate-Partner zu, sobald eine
 * Session existiert und ein gültiger Referral-Code im Storage liegt.
 *
 * Feuert genau einmal pro User-Session (useRef-Guard gegen StrictMode-
 * Doppel-Mount). Der Server-Endpoint ist zusätzlich idempotent
 * (first-touch via UNIQUE(user_id)), also schadet ein Doppel-Call nicht.
 *
 * Best-effort: Fehler werden geschluckt — Attribution darf den App-Flow
 * nie blockieren.
 */
export function useAffiliateAttribution(user: User | null): void {
  const attributedFor = useRef<string | null>(null);

  useEffect(() => {
    if (!user || !supabase) return;
    if (attributedFor.current === user.id) return;

    const code = getStoredRef();
    if (!code) return;

    attributedFor.current = user.id;

    void (async () => {
      try {
        const { data } = await supabase!.auth.getSession();
        const token = data.session?.access_token;
        if (!token) {
          // Noch kein Token — beim nächsten Render erneut versuchen.
          attributedFor.current = null;
          return;
        }
        await fetch('/api/affiliate-attribute', {
          method: 'POST',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ code }),
        });
        // Code bewusst NICHT löschen — Server ist idempotent, und falls
        // die Zuordnung fehlschlug (z. B. Netzfehler), greift sie beim
        // nächsten Login erneut. Läuft sonst nach dem Window von selbst ab.
      } catch {
        // best-effort — beim nächsten Login erneut versuchen
        attributedFor.current = null;
      }
    })();
  }, [user]);
}
